import { useState, useEffect, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Range } from 'react-range';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import styles from '@features/categories/components/CategoriesFilterRange/CategoriesFilterRange.styles';
import FilterRangeState from '@features/categories/mobx/state/FilterRangeState';
import { useResponsive } from '@features/responsive';
import CategoriesFilterRangeThumb from '@features/categories/components/CategoriesFilterRange/components/CategoriesFilterRangeThumb';
import CategoriesFilterRangeTrack from '@features/categories/components/CategoriesFilterRange/components/CategoriesFilterRangeTrack';
type PropsType = {
  filter: FilterRangeState;
  dropdown?: boolean;
  forceRefresh?: boolean;
};
const CategoriesFilterRange = observer(({
  filter,
  forceRefresh,
  dropdown = false
}: PropsType) => {
  const [localValue, setLocalValue] = useState<{
    min: string;
    max: string;
  }>({
    min: filter.value.min.toString(),
    max: filter.value.max.toString()
  });
  const [value, setValue] = useState<number[]>([filter.value.min, filter.value.max]);
  const [hasError, setHasError] = useState(false);
  const parseValue = num => parseInt(num, 10);
  const {
    t
  } = useTranslation('filters');
  const rangeValue = useMemo(() => {
    const {
      min: defaultMin,
      max: defaultMax
    } = filter.defaultValue;
    const [min, max] = value;
    let newMin = parseValue(min);
    let newMax = parseValue(max);
    if (min < defaultMin) newMin = defaultMin;
    if (max > defaultMax) newMax = defaultMax;
    if (min > defaultMax) newMin = defaultMax;
    if (max < defaultMin) newMax = defaultMin;
    if (min > max && max > defaultMin) {
      newMin = max;
      newMax = max;
    }
    return [newMin, newMax];
  }, [filter]);
  const {
    isNarrowerThanTablet
  } = useResponsive();
  const isValid = v => !!v && new RegExp('^[0-9]*$').test(v) && v.toString().length < 7;
  const setFilterValue = useCallback(debounce((filterValues: {
    min: number;
    max: number;
  }) => {
    filter.setValue(filterValues);
  }, 10), [filter.setValue]);
  const onInputMinChange = event => {
    if (hasError) {
      setHasError(false);
    }
    const {
      max
    } = filter.value;
    const min = parseValue(event.target.value);
    if (min > max) {
      setHasError(true);
    }
    const newValue = {
      min,
      max
    };
    setValue([newValue.min, newValue.max]);
    setFilterValue(newValue);
  };
  const onInputMaxChange = event => {
    if (hasError) {
      setHasError(false);
    }
    const {
      min
    } = filter.value;
    const max = parseValue(event.target.value);
    if (min > max) {
      setHasError(true);
    }
    const newValue = {
      min,
      max
    };
    setValue([newValue.min, newValue.max]);
    setFilterValue(newValue);
  };
  const onChange = val => {
    let min = val[0];
    let max = val[1];
    if (!isValid(min) || !isValid(max) || min === '' || max === '') {
      return;
    }
    const parsedMin = parseValue(min);
    const parsedMax = parseValue(max);
    if (parsedMax < parsedMin) {
      min = parsedMax;
    }
    if (parsedMin > parsedMax) {
      max = parsedMin;
    }
    if (parsedMin < filter.defaultValue.min) {
      min = filter.defaultValue.min;
    }
    if (parsedMax > filter.defaultValue.max) {
      max = filter.defaultValue.max;
    }
    setValue([parseValue(min), parseValue(max)]);
    setFilterValue({
      min: parseValue(min),
      max: parseValue(max)
    });
    setLocalValue({
      min: min.toString(),
      max: max.toString()
    });
  };
  const renderTrack = ({
    props,
    children
  }) => <CategoriesFilterRangeTrack ref={props.ref} props={props} filter={filter} value={value}>
      {children}
    </CategoriesFilterRangeTrack>;
  const renderThumb = ({
    props,
    isDragged
  }) => <CategoriesFilterRangeThumb key={`thumb${Math.random()}`} props={props} isDragged={isDragged} />;
  const shouldRenderRangeComponent = !dropdown && isNarrowerThanTablet && filter.defaultValue.min !== filter.defaultValue.max;
  useEffect(() => {
    if (forceRefresh) {
      setValue([parseValue(filter.value.min), parseValue(filter.value.max)]);
      setLocalValue({
        min: filter.value.min.toString(),
        max: filter.value.max.toString()
      });
    }
  }, [forceRefresh]);
  return <div sx={styles.filterContainer(dropdown)} data-cy="CategoriesFilterRange">
      <div sx={styles.inputContainer}>
        <input sx={styles.numberInput(hasError)} type="number" value={localValue.min} onChange={e => setLocalValue({
        ...localValue,
        min: e.target.value
      })} onBlur={onInputMinChange} maxLength={6} />
        <span sx={styles.dash} />
        <input sx={styles.numberInput(hasError)} type="number" value={localValue.max} onChange={e => setLocalValue({
        ...localValue,
        max: e.target.value
      })} onBlur={onInputMaxChange} maxLength={6} />
        {filter.unit && <span sx={styles.unit}>{filter.currency?.symbol ? filter.currency.symbol : filter.unit}</span>}
      </div>
      {hasError && <div sx={styles.rangeError}>{t('rangeError')}</div>}
      {shouldRenderRangeComponent && <div data-cy="CategoriesFilterRangeInput">
          {/* @ts-expect-error - react-range Direction type issue */}
          <Range min={filter.defaultValue.min} max={filter.defaultValue.max} step={1} values={rangeValue} allowOverlap onChange={onChange} renderTrack={renderTrack} renderThumb={renderThumb} draggableTrack={false} disabled={false} rtl={false} />
        </div>}
    </div>;
});
export default CategoriesFilterRange;